import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  message,
  Modal,
  Form,
  Typography,
  Popconfirm,
  Select,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  KeyOutlined,
  ReloadOutlined,
  EditOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { panelTokenApiService } from '../services/panelTokenApiService';
import type { PanelToken } from '../types/panel-token.types';
import { getAllPanels, type PanelBasicInfo } from '@/features/panels/services/panelApiService';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { formatDateTime } from '@/shared/utils/dateFormatter';

const { Text } = Typography;

/**
 * コンポーネント定数
 */
const COMPONENT_CONSTANTS = {
  TOKEN_LENGTH: 64,
  TOKEN_PATTERN: /^[a-f0-9]{64}$/,
  TABLE_WIDTH_ID: 80,
  TABLE_WIDTH_ID_MOBILE: 60,
  TABLE_WIDTH_PANEL: 150,
  TABLE_WIDTH_STATUS: 80,
  TABLE_WIDTH_ACTION: 180,
  TABLE_WIDTH_ACTION_MOBILE: 120,
  PAGINATION_PAGE_SIZE_DESKTOP: 10,
  PAGINATION_PAGE_SIZE_MOBILE: 5,
} as const;

/**
 * 盤トークン追加フォーム値
 */
interface AddTokenFormValues {
  readonly panelId: number;
  readonly token: string;
}

/**
 * 盤トークン更新フォーム値
 */
interface UpdateTokenFormValues {
  readonly token: string;
}

/**
 * 盤認証トークン管理コンポーネント
 */
export const PanelTokenManager: React.FC = () => {
  const [tokens, setTokens] = useState<readonly PanelToken[]>([]);
  const [panels, setPanels] = useState<readonly PanelBasicInfo[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingToken, setEditingToken] = useState<PanelToken | null>(null);
  const [form] = Form.useForm<AddTokenFormValues>();
  const [editForm] = Form.useForm<UpdateTokenFormValues>();
  const { isMobile } = useResponsive();

  /**
   * トークン一覧を取得
   */
  const fetchTokens = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const fetchedTokens = await panelTokenApiService.getAll();
      setTokens([...fetchedTokens]);
    } catch (error) {
      message.error('盤トークンの取得に失敗しました');
      console.error('Failed to fetch panel tokens:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * 盤一覧を取得
   */
  const fetchPanels = useCallback(async (): Promise<void> => {
    try {
      const fetchedPanels = await getAllPanels();
      setPanels([...fetchedPanels]);
    } catch (error) {
      message.error('盤一覧の取得に失敗しました');
      console.error('Failed to fetch panels:', error);
    }
  }, []);

  /**
   * トークンを登録
   */
  const handleAddToken = async (values: AddTokenFormValues): Promise<void> => {
    try {
      await panelTokenApiService.register({
        panelId: values.panelId,
        token: values.token.trim(),
      });
      message.success('トークンを登録しました');
      setIsModalOpen(false);
      form.resetFields();
      await fetchTokens();
    } catch (error: unknown) {
      if (error instanceof Error) {
        message.error(error.message || 'トークンの登録に失敗しました');
      } else {
        message.error('トークンの登録に失敗しました');
      }
      console.error('Failed to add token:', error);
    }
  };

  /**
   * トークンを更新
   */
  const handleUpdateToken = async (values: UpdateTokenFormValues): Promise<void> => {
    if (editingToken === null) {
      return;
    }

    try {
      await panelTokenApiService.update(editingToken.panelId, values.token.trim());
      message.success('トークンを更新しました');
      setIsEditModalOpen(false);
      setEditingToken(null);
      editForm.resetFields();
      await fetchTokens();
    } catch (error: unknown) {
      if (error instanceof Error) {
        message.error(error.message || 'トークンの更新に失敗しました');
      } else {
        message.error('トークンの更新に失敗しました');
      }
      console.error('Failed to update token:', error);
    }
  };

  /**
   * トークンを無効化
   */
  const handleDisableToken = async (panelId: number): Promise<void> => {
    try {
      await panelTokenApiService.disable(panelId);
      message.success('トークンを無効化しました');
      await fetchTokens();
    } catch (error: unknown) {
      if (error instanceof Error) {
        message.error(error.message || 'トークンの無効化に失敗しました');
      } else {
        message.error('トークンの無効化に失敗しました');
      }
      console.error('Failed to disable token:', error);
    }
  };

  /**
   * トークンを有効化
   */
  const handleEnableToken = async (panelId: number): Promise<void> => {
    try {
      await panelTokenApiService.enable(panelId);
      message.success('トークンを有効化しました');
      await fetchTokens();
    } catch (error: unknown) {
      if (error instanceof Error) {
        message.error(error.message || 'トークンの有効化に失敗しました');
      } else {
        message.error('トークンの有効化に失敗しました');
      }
      console.error('Failed to enable token:', error);
    }
  };

  /**
   * トークンを削除
   */
  const handleDeleteToken = async (panelId: number): Promise<void> => {
    try {
      await panelTokenApiService.delete(panelId);
      message.success('トークンを削除しました');
      await fetchTokens();
    } catch (error: unknown) {
      if (error instanceof Error) {
        message.error(error.message || 'トークンの削除に失敗しました');
      } else {
        message.error('トークンの削除に失敗しました');
      }
      console.error('Failed to delete token:', error);
    }
  };

  /**
   * モーダルを開く
   */
  const handleOpenModal = (): void => {
    setIsModalOpen(true);
  };

  /**
   * モーダルを閉じる
   */
  const handleCloseModal = (): void => {
    setIsModalOpen(false);
    form.resetFields();
  };

  /**
   * 編集モーダルを開く
   */
  const handleOpenEditModal = (token: PanelToken): void => {
    setEditingToken(token);
    editForm.setFieldsValue({ token: '' });
    setIsEditModalOpen(true);
  };

  /**
   * 編集モーダルを閉じる
   */
  const handleCloseEditModal = (): void => {
    setIsEditModalOpen(false);
    setEditingToken(null);
    editForm.resetFields();
  };

  /**
   * トークン検証ルール
   */
  const validateToken = (_: unknown, value: string): Promise<void> => {
    if (value === null || value === undefined || value.trim() === '') {
      return Promise.reject(new Error('トークンを入力してください'));
    }

    const trimmedValue = value.trim();

    if (trimmedValue.length !== COMPONENT_CONSTANTS.TOKEN_LENGTH) {
      return Promise.reject(
        new Error(`トークンは${COMPONENT_CONSTANTS.TOKEN_LENGTH}文字でなければなりません`)
      );
    }

    if (!COMPONENT_CONSTANTS.TOKEN_PATTERN.test(trimmedValue)) {
      return Promise.reject(
        new Error('トークンは小文字の16進数文字列（a-f, 0-9）でなければなりません')
      );
    }

    return Promise.resolve();
  };

  /**
   * 日時フォーマット
   */
  const formatLastUsedAt = (lastUsedAt: string | null): string => {
    if (lastUsedAt === null) {
      return '未使用';
    }
    return formatDateTime(new Date(lastUsedAt));
  };

  /**
   * 初回データ取得
   */
  useEffect(() => {
    fetchTokens();
    fetchPanels();
  }, [fetchTokens, fetchPanels]);

  const columns: ColumnsType<PanelToken> = [
    ...(!isMobile ? [{
      title: '盤ID',
      dataIndex: 'panelId',
      key: 'panelId',
      width: COMPONENT_CONSTANTS.TABLE_WIDTH_ID,
    }] : []),
    {
      title: '盤名',
      key: 'panelName',
      width: COMPONENT_CONSTANTS.TABLE_WIDTH_PANEL,
      render: (_: unknown, record: PanelToken) => (
        <Text>{record.panel?.panelName || `盤${record.panelId}`}</Text>
      ),
    },
    ...(!isMobile ? [{
      title: '状態',
      dataIndex: 'isActive',
      key: 'isActive',
      width: COMPONENT_CONSTANTS.TABLE_WIDTH_STATUS,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '有効' : '無効'}
        </Tag>
      ),
    }] : []),
    ...(!isMobile ? [{
      title: '最終使用',
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      render: (lastUsedAt: string | null) => (
        <Text type="secondary">
          {formatLastUsedAt(lastUsedAt)}
        </Text>
      ),
    }] : []),
    {
      title: '操作',
      key: 'action',
      width: isMobile ? COMPONENT_CONSTANTS.TABLE_WIDTH_ACTION_MOBILE : COMPONENT_CONSTANTS.TABLE_WIDTH_ACTION,
      render: (_: unknown, record: PanelToken) => (
        <Space size="small">
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleOpenEditModal(record)}
          >
            {!isMobile && '更新'}
          </Button>
          {record.isActive ? (
            <Popconfirm
              title="トークンを無効化"
              description={`「${record.panel?.panelName || `盤${record.panelId}`}」のトークンを無効化してもよろしいですか？`}
              onConfirm={() => handleDisableToken(record.panelId)}
              okText="無効化"
              cancelText="キャンセル"
            >
              <Button
                icon={<StopOutlined />}
                size="small"
              >
                {!isMobile && '無効化'}
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="トークンを有効化"
              description={`「${record.panel?.panelName || `盤${record.panelId}`}」のトークンを有効化してもよろしいですか？`}
              onConfirm={() => handleEnableToken(record.panelId)}
              okText="有効化"
              cancelText="キャンセル"
            >
              <Button
                icon={<CheckCircleOutlined />}
                size="small"
                type="primary"
              >
                {!isMobile && '有効化'}
              </Button>
            </Popconfirm>
          )}
          <Popconfirm
            title="トークンを削除"
            description={`「${record.panel?.panelName || `盤${record.panelId}`}」のトークンを削除してもよろしいですか？`}
            onConfirm={() => handleDeleteToken(record.panelId)}
            okText="削除"
            cancelText="キャンセル"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              {!isMobile && '削除'}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={
          <Space>
            <KeyOutlined />
            {!isMobile && '盤認証トークン'}
            {isMobile && 'トークン'}
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchTokens}
              loading={isLoading}
            >
              {!isMobile && '更新'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenModal}
            >
              {!isMobile && '追加'}
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={[...tokens]}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: isMobile ? COMPONENT_CONSTANTS.PAGINATION_PAGE_SIZE_MOBILE : COMPONENT_CONSTANTS.PAGINATION_PAGE_SIZE_DESKTOP,
            showTotal: (total) => `合計 ${total} 件`,
            showSizeChanger: !isMobile,
          }}
          scroll={isMobile ? { x: true } : undefined}
          size={isMobile ? 'small' : 'middle'}
        />
      </Card>

      <Modal
        title="盤トークンを追加"
        open={isModalOpen}
        onCancel={handleCloseModal}
        onOk={() => form.submit()}
        okText="追加"
        cancelText="キャンセル"
        width={isMobile ? '90%' : 600}
      >
        <Form form={form} layout="vertical" onFinish={handleAddToken}>
          <Form.Item
            name="panelId"
            label="盤"
            rules={[
              { required: true, message: '盤を選択してください' },
            ]}
          >
            <Select
              placeholder="盤を選択"
              showSearch
              optionFilterProp="children"
            >
              {panels.map((panel) => (
                <Select.Option key={panel.id} value={panel.id}>
                  {panel.panelName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="token"
            label="トークン（64文字の16進数文字列）"
            rules={[
              { validator: validateToken },
            ]}
            extra="トークンは小文字のa-f, 0-9のみで構成される64文字の文字列である必要があります"
          >
            <Input.TextArea
              placeholder="a4f5b8c9d2e1f3a7b5c8d9e2f4a6b8c0d1e3f5a7b9c2d4e6f8a0b2c4d6e8f0a2"
              maxLength={COMPONENT_CONSTANTS.TOKEN_LENGTH}
              showCount
              rows={3}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="盤トークンを更新"
        open={isEditModalOpen}
        onCancel={handleCloseEditModal}
        onOk={() => editForm.submit()}
        okText="更新"
        cancelText="キャンセル"
        width={isMobile ? '90%' : 600}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdateToken}>
          <Form.Item label="盤">
            <Text strong>
              {editingToken?.panel?.panelName || `盤${editingToken?.panelId}`}
            </Text>
          </Form.Item>
          <Form.Item
            name="token"
            label="新しいトークン（64文字の16進数文字列）"
            rules={[
              { validator: validateToken },
            ]}
            extra="トークンは小文字のa-f, 0-9のみで構成される64文字の文字列である必要があります"
          >
            <Input.TextArea
              placeholder="a4f5b8c9d2e1f3a7b5c8d9e2f4a6b8c0d1e3f5a7b9c2d4e6f8a0b2c4d6e8f0a2"
              maxLength={COMPONENT_CONSTANTS.TOKEN_LENGTH}
              showCount
              rows={3}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
